import * as CL from '@design-system/component-library';
import { monthMsg, t } from '../../common/i18n/index.js';
import { eurosToCents, formatSum } from '../../common/utils/priceUtils.js';
import { getLocalizedString, getProductOfferingPriceDataFromType } from '../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import { ProductDetailsQuantity } from '../ProductDetails/partials/ProductDetailsQuantity.js';
import { ProductOfferAccordionInfo } from '../ProductOfferingAddonCoCo/ProductOfferAddOnCoCo.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco.js';
import type { ChangeEvent } from 'react';

import './DeviceDetailsCommercialAddonCoco.scss';

export interface DeviceDetailsCommercialAddonCocoProps {
  addon: ProductOfferingCoco;
  isChecked: boolean;
  quantity: number;
  onToggle: (checked: boolean) => void;
  onQuantityChange: (quantity: number) => void;
  hideQuantitySelector?: boolean;
  maxQuantity?: number;
  additionalInfo?: string;
  disabled?: boolean;
}

export const DeviceDetailsCommercialAddonCoco = ({
  addon,
  isChecked,
  quantity,
  onToggle,
  onQuantityChange,
  hideQuantitySelector = false,
  maxQuantity = 1,
  additionalInfo,
  disabled = false,
}: DeviceDetailsCommercialAddonCocoProps) => {
  const addonName = getLocalizedString(addon.name);

  const recurringPriceWithoutTax = eurosToCents(
    getProductOfferingPriceDataFromType(addon.productOfferingPrice, PriceType.RECURRING_CHARGE)?.priceWithoutTax ?? 0
  );

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    onToggle(event.currentTarget.checked);
  };

  const addonDescription = addon.description ? getLocalizedString(addon.description).trim() : '';
  const canChangeQuantity = isChecked && maxQuantity > 1;
  const quantityDisabled = disabled || !canChangeQuantity;

  const contentClassName = hideQuantitySelector
    ? 'of-product-details-commercial-addons__content of-product-details-commercial-addons__content--no-quantity'
    : 'of-product-details-commercial-addons__content';

  return (
    <div className="of-product-details-commercial-addons__item">
      <div className="of-product-details-commercial-addons__header ds-margin-bottom--3">
        <CL.Checkbox
          key={`${addon.id}-${isChecked ? 'checked' : 'unchecked'}-${disabled ? 'disabled' : 'enabled'}`}
          id={`addon-${addon.id}`}
          checked={isChecked}
          disabled={disabled}
          onChange={handleCheckboxChange}
        >
          <span className="of-product-details-commercial-addons__sr-only">{addonName}</span>
        </CL.Checkbox>

        <div className="of-product-details-commercial-addons__details">
          <span className="of-product-details-commercial-addons__name ds-font-size--big ds-font-weight--600">
            {addonName}
          </span>
          {addonDescription ? (
            <ProductOfferAccordionInfo id={`addon-description-${addon.id}`} description={addonDescription} />
          ) : null}
        </div>
      </div>

      <div className={contentClassName}>
        <div className="of-product-details-commercial-addons__price ds-margin-top--3">
          <CL.Price
            currentPrice={formatSum(recurringPriceWithoutTax, true)}
            currentPriceUnit={`€/${t.XXVX(monthMsg)}`}
            additionalInfo={
              additionalInfo ? (
                <span className="of-product-details-commercial-addons__additional-info">{additionalInfo}</span>
              ) : undefined
            }
          />
        </div>
        {hideQuantitySelector ? null : (
          <div className="of-product-details-commercial-addons__quantity ds-margin-top--3">
            <ProductDetailsQuantity
              quantity={isChecked ? quantity : 1}
              setQuantity={onQuantityChange}
              maxValue={maxQuantity}
              disabled={quantityDisabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};
